

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { imageExist } from '../../assets/js/init';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';

const ArthaPrivacyPolicy = () => {
	// Theme-like constants based on your original CSS variables
	const colors = {
		bg: '#FAFAF8',
		surface: '#FFFFFF',
		textPrimary: '#1A1A1A',
		textSecondary: '#5A5A5A',
		textMuted: '#8A8A8A',
		accent: '#4A2DBE',
		accentLight: '#F0ECFA',
		border: '#E8E8E4',
		green: '#2D8B4E',
		greenLight: '#EDF7F0'
	};

	const serifFont = "'DM Serif Display', serif";
	const sansFont = "'DM Sans', sans-serif";

	const theme    = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md')); 

	const location = useLocation();

	// useEffect(() => {
	// 	window.scrollTo(0, 0);
	// }, [location]);

	// const location = useLocation();

	useEffect(() => {
		// Method 1: Immediate scroll
		window.scrollTo(0, 0);
		document.documentElement.scrollTop = 0;
		document.body.scrollTop = 0;

		// Method 2: Scroll after a small delay for DOM rendering
		const timer = setTimeout(() => {
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;
		}, 0);

		// Method 3: Using requestAnimationFrame for better performance
		const animationFrame = requestAnimationFrame(() => {
			window.scrollTo(0, 0);
			document.documentElement.scrollTop = 0;
			document.body.scrollTop = 0;
		});

		return () => {
			clearTimeout(timer);
			cancelAnimationFrame(animationFrame);
		};
	}, [location]);


	return (
		<Box sx={{ bgcolor: colors.bg, minHeight: '100vh', fontFamily: sansFont }}>
			{/* Header */}
			<AppBar 
				position="sticky" 
				elevation={0} 
				sx={{ 
					bgcolor: colors.surface, 
					borderBottom: `1px solid ${colors.border}`,
					zIndex: 1100 
				}}
			>
				<Container maxWidth="md" sx={{ px: { xs: 3, md: 0 } }}>
					<Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 0 } }}>
						<Link href='/'>
							<Box className='displayCenter' sx={{ gap: '10px' }}>
								{imageExist({
									required  : '/logo',
									url       : '/logo.svg',
									alt       : 'logo',
									height    : isMobile ? '25px' : '30px'
								})}
								{imageExist({
									required  : '/logo',
									url       : '/text.svg',
									alt       : 'logo',
									height    : isMobile ? '15px' : '20px'
								})}
							</Box>
						</Link>
						<Box sx={{ 
							fontSize: '12px', 
							fontWeight: 600, 
							color: colors.accent, 
							bgcolor: colors.accentLight, 
							px: 1.8, 
							py: 0.8, 
							borderRadius: '20px', 
							textTransform: 'uppercase',
							letterSpacing: '0.5px'
						}}>
							Privacy Policy
						</Box>
					</Toolbar>
				</Container>
			</AppBar>

			<Container maxWidth="md" sx={{ px: { xs: 3, md: 0 } }}>
				{/* Hero Section */}
				<Box sx={{ pt: 7, pb: 5 }}>
					<Typography variant="h1" sx={{ 
						fontFamily: serifFont, 
						fontSize: { xs: '32px', md: '44px !important' }, 
						color: colors.textPrimary, 
						mb: 2,
						lineHeight: 1.2
					}}>
						Privacy Policy
					</Typography>
					<Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', mb: 1.5, color: colors.textMuted, fontSize: '14px' }}>
						<Box component="span"><strong>Effective Date:</strong> April 13, 2026</Box>
						<Box component="span"><strong>Last Updated:</strong> April 13, 2026</Box>
						<Box component="span"><strong>Version:</strong> 1.0</Box>
					</Box>
					<Typography sx={{ fontSize: '17px', color: colors.textSecondary, mt: 2.5, lineHeight: 1.7 }}>
						Artha (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is a product of <strong>CafsInfotech</strong>. We are committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use the Artha mobile application (the &quot;App&quot;), available on Google Play Store and Apple App Store.
					</Typography>
				</Box>

				{/* Table of Contents */}
				<Box sx={{ bgcolor: colors.surface, border: `1px solid ${colors.border}`, borderRadius: '12px', p: { xs: 3, md: 4 }, mb: 6 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '20px', mb: 2, color: colors.textPrimary }}>
						Table of Contents
					</Typography>
					<Box 
						component="ol" 
						sx={{ 
							m: 0, 
							p: 0, 
							listStyle: 'none', 
							columnCount: { xs: 1, md: 2 }, 
							columnGap: 4,
							'& li': { 
								counterIncrement: 'toc', 
								mb: 1,
								display: 'flex',
								alignItems: 'baseline',
								'&::before': {
									content: 'counter(toc, decimal-leading-zero) "."',
									color: colors.accent,
									fontWeight: 600,
									fontSize: '13px',
									mr: 1
								}
							}
						}}
					>
						{[
							{ label: 'Information We Collect', id: 's1' },
							{ label: 'How We Use Your Information', id: 's2' },
							{ label: 'Data Storage & Security', id: 's3' },
							{ label: 'Third-Party Services', id: 's4' },
							{ label: 'Advertising & Analytics', id: 's5' },
							{ label: 'Children\'s Privacy', id: 's6' },
							{ label: 'Your Rights & Choices', id: 's7' },
							{ label: 'Data Retention', id: 's8' },
							{ label: 'International Data Transfers', id: 's9' },
							{ label: 'Changes to This Policy', id: 's10' },
							{ label: 'Contact Us', id: 's11' },
							{ label: 'Grievance Officer', id: 's12' }
						].map((item) => (
							<li key={item.id}>
								<Link href={`#${item.id}`} sx={{ color: colors.textSecondary, textDecoration: 'none', fontSize: '14px', '&:hover': { color: colors.accent } }}>
									{item.label}
								</Link>
							</li>
						))}
					</Box>
				</Box>

				{/* Core Promise Box */}
				<Box sx={{ bgcolor: colors.greenLight, borderLeft: `3px solid ${colors.green}`, p: '16px 20px', borderRadius: '0 8px 8px 0', mb: 5 }}>
					<Typography sx={{ color: '#1A3D2A', m: 0 }}>
						<strong>Our Core Promise:</strong> Artha does not track you, does not store your personal data on our servers, and does not sell your information to anyone. Your financial calculations stay on your device.
					</Typography>
				</Box>

				{/* Section 1 */}
				<Box component="section" id="s1" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						1. Information We Collect
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>We believe in minimal data collection. Artha is designed to function with little to no personal data. Below is a transparent breakdown of the information we may collect:</Typography>
          
					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>a) Information You Provide Directly</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>
						Artha is primarily an offline financial calculator app. We do <strong>not</strong> require you to create an account, log in, or provide personal information such as your name, email address, phone number, or financial details to use the App&apos;s core features.
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>
						If you contact us for support or feedback (e.g., via email), we may collect your name, email address, and the content of your communication solely to respond to your inquiry.
					</Typography>

					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>b) Information Collected Automatically</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 1 }}>When you use the App, certain technical information may be collected automatically, including:</Typography>
					<Box component="ul" sx={{ ml: 3, mb: 2, color: colors.textSecondary, '& li': { mb: 0.8 } }}>
						<li><strong>Device Information:</strong> Device model, operating system version, unique device identifiers (e.g., Android Advertising ID, Apple IDFA — only if you have not opted out), screen resolution, and language settings.</li>
						<li><strong>Usage Data:</strong> App usage statistics such as which calculators are used, frequency of use, session duration, crash logs, and performance data.</li>
						<li><strong>Log Data:</strong> IP address (anonymized), app version, time and date of access, and referring/exit pages.</li>
					</Box>

					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>c) Information We Do NOT Collect</Typography>
					<Box sx={{ bgcolor: colors.accentLight, borderLeft: `3px solid ${colors.accent}`, p: '16px 20px', borderRadius: '0 8px 8px 0', my: 2.5 }}>
						<Typography sx={{ color: colors.textPrimary, fontWeight: 500 }}>
							We do NOT collect your financial inputs, calculation results, bank details, Aadhaar/PAN numbers, passwords, contacts, photos, location data, call logs, SMS, or any sensitive personal information.
						</Typography>
					</Box>
				</Box>

				{/* Section 2 */}
				<Box component="section" id="s2" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						2. How We Use Your Information
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>The limited information we collect is used solely for the following purposes:</Typography>
					<TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colors.border}`, mb: 2 }}>
						<Table size="small">
							<TableHead sx={{ bgcolor: colors.accentLight }}>
								<TableRow>
									<TableCell sx={{ fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', color: colors.textPrimary }}>Purpose</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', color: colors.textPrimary }}>Legal Basis</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{[
									['To provide and maintain the App\'s functionality', 'Performance of service'],
									['To improve, optimize, and enhance the App experience', 'Legitimate interest'],
									['To identify and fix bugs, crashes, and technical issues', 'Legitimate interest'],
									['To understand general usage patterns and popular features', 'Legitimate interest'],
									['To respond to your support requests or feedback', 'Consent / Legitimate interest'],
									['To comply with legal obligations', 'Legal requirement']
								].map((row, index) => (
									<TableRow key={index}>
										<TableCell sx={{ color: colors.textSecondary }}>{row[0]}</TableCell>
										<TableCell sx={{ color: colors.textSecondary }}>{row[1]}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
					<Typography sx={{ color: colors.textSecondary }}>We do <strong>not</strong> use your data for profiling, automated decision-making, or selling to third parties.</Typography>
				</Box>

				{/* Section 3 */}
				<Box component="section" id="s3" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						3. Data Storage & Security
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>
						All financial calculation data (your inputs, results, reports, and charts) are stored <strong>locally on your device only</strong>. We do not transmit, upload, or store your calculation data on any external server.
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 1 }}>For the limited data we do process (crash logs, analytics), we implement industry-standard security measures including:</Typography>
					<Box component="ul" sx={{ ml: 3, mb: 2, color: colors.textSecondary, '& li': { mb: 0.8 } }}>
						<li>Encryption of data in transit using TLS/SSL protocols</li>
						<li>Access controls and authentication for internal systems</li>
						<li>Regular security assessments and vulnerability testing</li>
						<li>Minimal data collection principles (data minimization)</li>
					</Box>
					<Typography sx={{ color: colors.textSecondary }}>
						While we strive to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security but are committed to maintaining the highest standards commercially practicable.
					</Typography>
				</Box>

				{/* Section 4 */}
				<Box component="section" id="s4" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						4. Third-Party Services
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>The App may integrate with the following third-party services that have their own privacy policies:</Typography>
          
					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>a) Google Play Services (Android)</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>Required for app distribution and updates. Subject to <span style={{ color: '#000' }}>Google&apos;s Privacy Policy.</span></Typography>

					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>b) Apple App Store Services (iOS)</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>Required for app distribution and updates. Subject to <span style={{ color: '#000' }}>Apple&apos;s Privacy Policy</span></Typography>
				</Box>

				{/* Section 5 & 6 (Combined for brevity in example, but keeping all content) */}
				<Box component="section" id="s5" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						5. Advertising & Analytics
					</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>Artha may display advertisements through third-party advertising networks. These networks may use cookies, device identifiers, and similar technologies to collect information for the purpose of serving ads that may be relevant to you.</Typography>
					<Typography sx={{ color: colors.textSecondary }}>Analytics tools may collect anonymized, aggregated data about how users interact with the App. This data does not personally identify you and is used solely to improve the App.</Typography>
				</Box>

				<Box component="section" id="s6" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						6. Children&apos;s Privacy
					</Typography>
					<Box sx={{ bgcolor: colors.accentLight, borderLeft: `3px solid ${colors.accent}`, p: '16px 20px', borderRadius: '0 8px 8px 0', mb: 2 }}>
						<Typography sx={{ color: colors.textPrimary, fontWeight: 500 }}>
							Artha is suitable for users of all ages as a financial education and calculator tool. However, we do not knowingly collect personal information from children under the age of 13.
						</Typography>
					</Box>
					<Typography sx={{ color: colors.textSecondary }}>If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately. We will take prompt steps to delete such information from our systems.</Typography>
				</Box>

				{/* Section 7 */}
				<Box component="section" id="s7" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						7. Your Rights & Choices
					</Typography>
					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>For All Users</Typography>
					<Box component="ul" sx={{ ml: 3, mb: 2, color: colors.textSecondary, '& li': { mb: 0.8 } }}>
						<li><strong>Access:</strong> Request a copy of any personal data we hold about you.</li>
						<li><strong>Correction:</strong> Request correction of inaccurate personal data.</li>
						<li><strong>Deletion:</strong> Request deletion of your personal data from our systems.</li>
						<li><strong>Opt-Out:</strong> Opt out of personalized advertising via device settings.</li>
						<li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
						<li><strong>Withdraw Consent:</strong> Withdraw consent at any time where processing is based on consent.</li>
					</Box>
					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>For Users in the EEA (GDPR)</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>You have the right to lodge a complaint with a supervisory authority. We process data based on legitimate interests and, where applicable, your consent.</Typography>
          
					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>For Users in California (CCPA / CPRA)</Typography>
					<Typography sx={{ color: colors.textSecondary, mb: 2 }}>California residents have the right to know what personal information is collected and opt out of its sale. <strong>We do not sell your personal information.</strong></Typography>

					<Typography variant="h3" sx={{ fontSize: '16px', fontWeight: 700, color: colors.textPrimary, mt: 2.5, mb: 1 }}>For Users in India (DPDP Act, 2023)</Typography>
					<Typography sx={{ color: colors.textSecondary }}>Indian residents have the right to access, correct, and erase their personal data. We process data in accordance with the Digital Personal Data Protection Act, 2023.</Typography>
				</Box>

				{/* Remaining Sections: 8, 9, 10, 11, 12 */}
				{[
					{ id: 's8', title: '8. Data Retention', content: 'Since Artha stores calculation data locally on your device, you have full control over this data. You can delete it at any time by clearing the App data or uninstalling the App. Support correspondence is retained for up to 12 months.' },
					{ id: 's9', title: '9. International Data Transfers', content: 'If you access the App from outside India, please be aware that any limited data collected may be processed in India. We ensure that cross-border data transfers comply with applicable laws.' },
					{ id: 's10', title: '10. Changes to This Privacy Policy', content: 'We may update this Privacy Policy from time to time. We will update the "Last Updated" date at the top of this page. Continued use of the App after changes constitutes acceptance.' }
				].map((sec) => (
					<Box component="section" id={sec.id} key={sec.id} sx={{ mb: 5 }}>
						<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
							{sec.title}
						</Typography>
						<Typography sx={{ color: colors.textSecondary }}>{sec.content}</Typography>
					</Box>
				))}

				{/* Contact Sections */}
				<Box component="section" id="s11" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						11. Contact Us
					</Typography>
					<Box sx={{ bgcolor: colors.greenLight, borderLeft: `3px solid ${colors.green}`, p: '16px 20px', borderRadius: '0 8px 8px 0', mb: 2 }}>
						<Typography sx={{ color: '#1A3D2A' }}>
							<strong>Artha</strong><br />
							Email: <Link href="mailto:support@arthaganik.com" sx={{ color: 'inherit' }}>support@arthaganik.com</Link><br />
							Website: <Link href="https://arthaganik.com" target="_blank" sx={{ color: 'inherit' }}>arthaganik.com</Link>
						</Typography>
					</Box>
				</Box>

				<Box component="section" id="s12" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						12. Grievance Officer (India)
					</Typography>
					<Box sx={{ bgcolor: colors.greenLight, borderLeft: `3px solid ${colors.green}`, p: '16px 20px', borderRadius: '0 8px 8px 0', mb: 2 }}>
						<Typography sx={{ color: '#1A3D2A' }}>
							<strong>Grievance Officer</strong><br />
							Artha Ghanik<br />
							Email: <Link href="mailto:support@arthaganik.com" sx={{ color: 'inherit' }}>support@arthaganik.com</Link>
						</Typography>
					</Box>
				</Box>

				{/* Compliance Table */}
				<Box component="section" id="appstore" sx={{ mb: 5 }}>
					<Typography variant="h2" sx={{ fontFamily: serifFont, fontSize: '24px', color: colors.textPrimary, mb: 2, pt: 1.5, borderTop: `1px solid ${colors.border}` }}>
						App Store & Play Store Compliance Summary
					</Typography>
					<TableContainer component={Paper} elevation={0} sx={{ border: `1px solid ${colors.border}` }}>
						<Table size="small">
							<TableHead sx={{ bgcolor: colors.accentLight }}>
								<TableRow>
									<TableCell sx={{ fontWeight: 600, fontSize: '13px', textTransform: 'uppercase' }}>Requirement</TableCell>
									<TableCell sx={{ fontWeight: 600, fontSize: '13px', textTransform: 'uppercase' }}>Our Compliance</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{[
									['Data collection disclosure', 'Fully disclosed in Sections 1 & 4'],
									['Purpose of data use', 'Outlined in Section 2'],
									['Third-party data sharing', 'Disclosed in Sections 4 & 5'],
									['Data security measures', 'Described in Section 3'],
									['Children\'s privacy (COPPA)', 'Addressed in Section 6'],
									['User rights (GDPR, CCPA, DPDP)', 'Detailed in Section 7']
								].map((row, index) => (
									<TableRow key={index}>
										<TableCell sx={{ color: colors.textSecondary }}>{row[0]}</TableCell>
										<TableCell sx={{ color: colors.textSecondary }}>{row[1]}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</Box>
			</Container>

			{/* Footer */}
			<Box sx={{ bgcolor: colors.textPrimary, color: '#999', py: 5, textAlign: 'center', fontSize: '14px' }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					&copy; {new Date().getFullYear()} <Link href="https://CafsInfotech.com" sx={{ color: '#ccc', textDecoration: 'none' }}>CafsInfotech</Link>. All Rights Reserved.
				</Typography>
				<Typography variant="body2">Artha — All Your Finance Tools, Simplified.</Typography>
			</Box>
		</Box>
	);
};

export default ArthaPrivacyPolicy;
